import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Home, ArrowLeft, Phone, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const popularPages = [
    { name: "Calculators", path: "/calculators", description: "Calculate your mortgage payments" },
    { name: "Pre-Approval", path: "/pre-approval", description: "Get pre-approved today" },
    { name: "Blog", path: "/blog", description: "Mortgage tips and insights" },
    { name: "Contact", path: "/contact", description: "Get in touch with us" },
  ];

  return (
    <Layout>
      <SEO
        title="Page Not Found | Atralend"
        description="The page you're looking for doesn't exist. Find your way back to our mortgage services."
      />
      
      <div className="min-h-[70vh] flex items-center justify-center py-16">
        <div className="container mx-auto px-4 text-center">
          {/* 404 Illustration */}
          <div className="mb-8 animate-fade-in">
            <div className="text-8xl sm:text-9xl font-bold text-primary/20 mb-4">404</div>
            <div className="w-24 h-1 bg-primary mx-auto rounded-full" />
          </div>

          {/* Message */}
          <div className="mb-8 animate-fade-in-up">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Oops! Page Not Found
            </h1>
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              The page you're looking for seems to have wandered off. Don't worry, we'll help you find your way back.
            </p>
          </div>

          {/* Primary Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 animate-fade-in-up">
            <Link to="/">
              <Button size="lg" className="gap-2 w-full sm:w-auto">
                <Home className="w-4 h-4" />
                Back to Home
              </Button>
            </Link>
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.history.back()}
              className="gap-2 w-full sm:w-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </Button>
          </div>

          {/* Popular Pages */}
          <div className="max-w-2xl mx-auto animate-fade-in-up">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Popular Pages
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {popularPages.map((page) => (
                <Link
                  key={page.path}
                  to={page.path}
                  className="p-4 bg-card border border-border rounded-lg hover:border-primary/50 hover:shadow-md transition-all text-left group"
                >
                  <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">
                    {page.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {page.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>

          {/* Contact Help */}
          <div className="mt-12 pt-8 border-t border-border animate-fade-in-up">
            <p className="text-muted-foreground mb-4">
              Still need help? Contact us directly.
            </p>
            <a href="tel:613-850-2237">
              <Button variant="outline" className="gap-2">
                <Phone className="w-4 h-4" />
                Call 613-850-2237
              </Button>
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NotFound;
